import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { fetchCart, updateCartItem, removeFromCart } from '../../store/slices/cartSlice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import styled from 'styled-components';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import ContextualCoachBanner from '../../components/onboarding/ContextualCoachBanner';
import CartReturnsPolicy from '../../components/items/CartReturnsPolicy';
import { useTranslation } from 'react-i18next';
import { notifyCartStockAdjustments } from '../../utils/cartStockAdjustments';

const CartContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 2rem;
`;

const CartContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CartItems = styled.div`
  background-color: var(--bg-primary);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  overflow: hidden;
`;

const CartItem = styled.div`
  display: flex;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const ItemImage = styled.div`
  width: 100px;
  height: 100px;
  background-color: var(--bg-secondary);
  border-radius: 0.375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-light);
  font-size: 2rem;
  flex-shrink: 0;
`;

const ItemDetails = styled.div`
  flex: 1;
  margin-left: 1rem;
  
  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const ItemTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.5rem;
`;

const ItemPrice = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.5rem;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const QuantityButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid var(--border-color);
  background-color: var(--bg-primary);
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: var(--bg-secondary);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 60px;
  height: 32px;
  text-align: center;
  border: 1px solid var(--border-color);
  border-radius: 0.25rem;
  font-size: 0.875rem;
`;

const RemoveButton = styled.button`
  background-color: var(--error-color);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #dc2626;
  }
`;

const CartSummary = styled.div`
  background-color: var(--bg-primary);
  border-radius: 0.5rem;
  box-shadow: var(--shadow-sm);
  padding: 1.5rem;
  height: fit-content;
  position: sticky;
  top: 2rem;
`;

const SummaryTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  
  &.total {
    font-weight: 700;
    font-size: 1.125rem;
    border-top: 1px solid var(--border-color);
    padding-top: 0.75rem;
    margin-top: 0.75rem;
  }
`;

const CheckoutButton = styled.button`
  display: block;
  width: 100%;
  background-color: var(--primary-color);
  color: white;
  border: none;
  text-align: center;
  padding: 1rem;
  border-radius: 0.375rem;
  font-weight: 600;
  margin-top: 1rem;
  transition: background-color 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background-color: var(--primary-dark);
  }
  
  &:disabled {
    background-color: var(--text-light);
    cursor: not-allowed;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--text-secondary);
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 2rem;
  }
`;

const BrowseButton = styled(Link)`
  background-color: var(--primary-color);
  color: white;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  font-weight: 600;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: var(--primary-dark);
  }
`;

const CartPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, total } = useSelector((state) => state.cart);

  useEffect(() => {
    dispatch(fetchCart()).then((action) => {
      if (fetchCart.fulfilled.match(action)) {
        notifyCartStockAdjustments(action.payload?.stock_adjustments, (msg) => toast.info(msg));
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleQuantityChange = (cartItemId, newQuantity) => {
    if (newQuantity < 1) return;
    dispatch(updateCartItem({ cartItemId, quantity: newQuantity }));
  };

  const handleRemoveItem = (cartItemId) => {
    dispatch(removeFromCart(cartItemId));
  };

  if (loading) {
    return <LoadingSpinner text={t('cart.loading')} />;
  }

  if (items.length === 0) {
    return (
      <CartContainer>
        <PageTitle>{t('cart.title')}</PageTitle>
        <EmptyCart>
          <h3>{t('cart.empty')}</h3>
          <p>{t('cart.emptyShopHint')}</p>
          <BrowseButton to="/pre-owned-items">{t('cart.browseItems')}</BrowseButton>
        </EmptyCart>
      </CartContainer>
    );
  }

  return (
    <CartContainer>
      <PageTitle>{t('cart.title')}</PageTitle>
      <ContextualCoachBanner coachId="cart_one_delivery" />

      <CartReturnsPolicy />
      
      <CartContent>
        <CartItems>
          {items.map((item) => {
            const itemDetails = item.item_details || {};
            const cartItemId = item.cart_item_id != null ? item.cart_item_id : item.listing_id;
            const isDesigner = item.line_kind === 'designer';
            const isBoutique = item.line_kind === 'boutique';
            const itemTitle =
              item.title ||
              itemDetails.title ||
              itemDetails.item_name ||
              (isDesigner ? 'Designer listing' : isBoutique ? 'Boutique listing' : 'Listing');
            const itemPrice = item.price_at_time || itemDetails.price || 0;
            const itemImage =
              itemDetails.image_url ||
              itemDetails.primary_image ||
              (itemDetails.images && itemDetails.images.length > 0
                ? (typeof itemDetails.images[0] === 'string'
                    ? itemDetails.images[0]
                    : itemDetails.images[0]?.image)
                : null);
            
            return (
              <CartItem key={cartItemId ?? `line-${item.listing_id ?? 'unknown'}`}>
                <ItemImage>
                  {itemImage ? (
                    <img 
                      src={itemImage} 
                      alt={itemTitle}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '0.375rem' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div style={{ 
                    display: itemImage ? 'none' : 'flex',
                    width: '100%', 
                    height: '100%', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    fontSize: '2rem',
                    color: 'var(--text-light)'
                  }}>
                    📦
                  </div>
                </ItemImage>
                
                <ItemDetails>
                  {isDesigner && (
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#7c3aed' }}>
                      Designer listing
                    </span>
                  )}
                  <ItemTitle>{itemTitle}</ItemTitle>
                  <ItemPrice>{formatItemPriceEgp(itemPrice)}</ItemPrice>
                  
                  <QuantityControl>
                    <QuantityButton
                      onClick={() => handleQuantityChange(cartItemId, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      -
                    </QuantityButton>
                    <QuantityInput
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleQuantityChange(cartItemId, parseInt(e.target.value, 10) || 1)}
                      min="1"
                    />
                    {/* Plus button removed as requested */}
                  </QuantityControl>
                  
                  <RemoveButton onClick={() => handleRemoveItem(cartItemId)}>
                    {t('cart.remove')}
                  </RemoveButton>
                </ItemDetails>
              </CartItem>
            );
          })}
        </CartItems>
        
        <CartSummary>
          <SummaryTitle>{t('cart.orderSummary')}</SummaryTitle>
          
          <SummaryRow>
            <span>{t('cart.subtotalItems', { count: items.length })}</span>
            <span>{formatItemPriceEgp(total)}</span>
          </SummaryRow>
          
          <SummaryRow>
            <span>{t('checkout.delivery')}</span>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{t('cart.deliveryAtCheckout')}</span>
          </SummaryRow>
          
          <SummaryRow className="total">
            <span>{t('checkout.total')}</span>
            <span>{formatItemPriceEgp(total)}</span>
          </SummaryRow>
          
          <CheckoutButton onClick={() => navigate('/checkout')}>
            {t('cart.proceedToCheckout')}
          </CheckoutButton>
        </CartSummary>
      </CartContent>
    </CartContainer>
  );
};

export default CartPage;
